import { useCallback, useEffect, useRef, useState } from "react";
import { useLatestRef } from "./useLatestRef";

/**
 * One itemised group of unsaved changes shown in the guard dialog
 * (e.g. ``{ count: 4, label: "rows with edited weekly NIS3" }`` renders
 * as "4 rows with edited weekly NIS3"). Module-agnostic — each module
 * builds its own lines from its draft state, pluralised by the caller.
 */
export interface DraftSummaryLine {
  /** Human-readable description of the group (already pluralised). */
  label: string;
  /** Optional count rendered before the label. */
  count?: number;
}

/**
 * Per-invocation label overrides for the guard dialog. A page usually
 * sets its default labels on ``<UnsavedChangesDialog>`` for its primary
 * guarded action (e.g. Apply) and overrides them per ``guard()`` call
 * for other draft-losing actions (e.g. route navigation).
 */
export interface UnsavedChangesGuardLabels {
  /** Consequence line, e.g. "Leaving this page discards your draft:". */
  description?: string;
  /** Save-first button label, e.g. "Save & leave". */
  saveLabel?: string;
  /** Discard button label, e.g. "Discard & leave". */
  discardLabel?: string;
}

/** Per-invocation options for ``guard()``. */
export interface UnsavedChangesGuardActionOptions {
  /**
   * Runs when the planner cancels (button, Esc, outside click, ×)
   * instead of proceeding — e.g. ``blocker.reset()`` when the guarded
   * action is a react-router navigation.
   */
  onCancel?: () => void;
  /** Label overrides for this invocation's dialog. */
  labels?: UnsavedChangesGuardLabels;
}

/** Options for {@link useUnsavedChangesGuard}. */
export interface UseUnsavedChangesGuardOptions {
  /**
   * Whether the module currently holds unsaved draft changes. While
   * true, guarded actions are intercepted and tab-close / reload gets
   * the browser's native "leave site?" prompt.
   */
  isDirty: boolean;
  /**
   * Builds the itemised summary shown in the dialog. Called at the
   * moment a guarded action is intercepted, so it always reflects the
   * draft state at click time. Optional — without it the dialog shows
   * only the consequence line.
   */
  summary?: () => DraftSummaryLine[];
  /**
   * The module's existing save flow. When provided, the dialog offers a
   * save-first button: on resolve the guarded action proceeds; on
   * reject the dialog stays open with the error surfaced and the draft
   * untouched.
   */
  onSaveFirst?: () => Promise<void>;
}

/**
 * Dialog-facing state returned by {@link useUnsavedChangesGuard} —
 * spread it onto ``<UnsavedChangesDialog>`` and add the page's default
 * labels (``description``, ``saveLabel``, ``discardLabel``) there.
 */
export interface UnsavedChangesDialogState {
  /** Whether the dialog is open (a guarded action is pending). */
  open: boolean;
  /** Itemised summary lines captured when the action was intercepted. */
  summary: DraftSummaryLine[];
  /** Label overrides captured from the intercepted ``guard()`` call. */
  labels?: UnsavedChangesGuardLabels;
  /** Whether the save-first flow is in flight. */
  isSaving: boolean;
  /** Error message from a failed save-first attempt; null when none. */
  saveError: string | null;
  /** Whether a save-first flow is available (``onSaveFirst`` given). */
  canSaveFirst: boolean;
  /** Runs the module's save; on success the pending action proceeds. */
  onSaveAndContinue: () => void;
  /** Drops the draft path: the pending action proceeds unsaved. */
  onDiscardAndContinue: () => void;
  /** Closes the dialog; nothing runs, the draft stays as-is. */
  onCancel: () => void;
}

/** Return shape of {@link useUnsavedChangesGuard}. */
export interface UnsavedChangesGuard {
  /**
   * Wraps a draft-losing action: runs it immediately when clean,
   * otherwise opens the summary dialog and defers it to the planner's
   * choice (save-first / discard / cancel). Stable identity across
   * renders (safe as an effect dependency); re-entrant calls while a
   * guarded action is already pending are ignored.
   */
  guard: (
    action: () => void,
    options?: UnsavedChangesGuardActionOptions
  ) => void;
  /** Spread onto ``<UnsavedChangesDialog>``. */
  dialogProps: UnsavedChangesDialogState;
}

/** Dialog-visible state captured when an action is intercepted. */
interface InterceptedState {
  summary: DraftSummaryLine[];
  labels?: UnsavedChangesGuardLabels;
}

/** A guarded action deferred to the planner's choice. */
interface PendingGuardAction {
  run: () => void;
  onCancel?: () => void;
}

/**
 * Native tab-close / reload prompt while dirty. Browsers ignore custom
 * text; preventDefault + legacy returnValue trigger the built-in
 * "Leave site?" dialog.
 */
function useBeforeUnloadPrompt(isDirty: boolean): void {
  useEffect(() => {
    if (!isDirty) return;
    function handleBeforeUnload(event: BeforeUnloadEvent): void {
      event.preventDefault();
      // Legacy Chrome/Edge still require returnValue to be set.
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return function cleanup() {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);
}

/**
 * Unsaved-changes guard (design A — summary modal).
 *
 * Generic, module-agnostic layer: a module hands over its dirty flag, a
 * summary builder, and (optionally) its save flow; the hook returns a
 * ``guard(action)`` wrapper for any action that would discard the draft
 * plus the state for the blocking ``<UnsavedChangesDialog>``. While
 * dirty it also intercepts browser tab-close / reload via
 * ``beforeunload`` (native prompt).
 *
 * Guarded actions are not just button presses — any draft-losing exit
 * qualifies. For in-app navigation, pass ``guard`` to ``useNavigationGuard``.
 *
 * @example Range plan
 * ```tsx
 * const { guard, dialogProps } = useUnsavedChangesGuard({
 *   isDirty: draftSummary.length > 0,
 *   summary: () => draftSummary,
 *   onSaveFirst: handleSave
 * });
 * // Guarded button: <FilterBar onApply={() => guard(handleApply)} … />
 * // <UnsavedChangesDialog {...dialogProps}
 * //   description="Applying a new context discards your draft:"
 * //   saveLabel="Save & apply" discardLabel="Discard & apply" />
 * ```
 */
export function useUnsavedChangesGuard({
  isDirty,
  summary,
  onSaveFirst
}: UseUnsavedChangesGuardOptions): UnsavedChangesGuard {
  const isDirtyRef = useLatestRef(isDirty);
  const summaryRef = useLatestRef(summary);
  const onSaveFirstRef = useLatestRef(onSaveFirst);

  // The intercepted action lives in a ref (functions don't drive
  // rendering); the dialog-visible snapshot is React state. Non-null
  // ``intercepted`` ⇔ the dialog is open.
  const pendingRef = useRef<PendingGuardAction | null>(null);
  const [intercepted, setIntercepted] = useState<InterceptedState | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useBeforeUnloadPrompt(isDirty);

  const close = useCallback(function close(): void {
    pendingRef.current = null;
    setIntercepted(null);
    setIsSaving(false);
    setSaveError(null);
  }, []);

  const guard = useCallback(
    function guard(
      action: () => void,
      options?: UnsavedChangesGuardActionOptions
    ): void {
      if (!isDirtyRef.current) {
        action();
        return;
      }
      // One pending action at a time — re-entrant calls (e.g. a blocker
      // effect re-firing while the dialog is already open) are no-ops.
      if (pendingRef.current) return;
      pendingRef.current = { run: action, onCancel: options?.onCancel };
      const buildSummary = summaryRef.current;
      setIntercepted({
        summary: buildSummary ? buildSummary() : [],
        labels: options?.labels
      });
      setIsSaving(false);
      setSaveError(null);
    },
    [isDirtyRef, summaryRef]
  );

  const handleCancel = useCallback(
    function handleCancel(): void {
      const pending = pendingRef.current;
      close();
      pending?.onCancel?.();
    },
    [close]
  );

  const handleDiscardAndContinue = useCallback(
    function handleDiscardAndContinue(): void {
      const pending = pendingRef.current;
      close();
      pending?.run();
    },
    [close]
  );

  const handleSaveAndContinue = useCallback(
    function handleSaveAndContinue(): void {
      async function saveThenContinue(): Promise<void> {
        const saveFirst = onSaveFirstRef.current;
        const pending = pendingRef.current;
        if (!saveFirst || !pending) return;
        setIsSaving(true);
        setSaveError(null);
        try {
          await saveFirst();
        } catch (error) {
          // Save failed: keep the dialog open with the error surfaced
          // and the draft untouched — the planner can retry, discard,
          // or cancel.
          setIsSaving(false);
          setSaveError(
            error instanceof Error && error.message.length > 0
              ? error.message
              : "Save failed — your draft is unchanged."
          );
          return;
        }
        close();
        pending.run();
      }
      void saveThenContinue();
    },
    [close, onSaveFirstRef]
  );

  return {
    guard,
    dialogProps: {
      open: intercepted !== null,
      summary: intercepted?.summary ?? [],
      labels: intercepted?.labels,
      isSaving,
      saveError,
      canSaveFirst: Boolean(onSaveFirst),
      onSaveAndContinue: handleSaveAndContinue,
      onDiscardAndContinue: handleDiscardAndContinue,
      onCancel: handleCancel
    }
  };
}
